import React, { useState } from 'react';
import { MessageSquare, Send, X, TrendingUp, BarChart3, AlertCircle, FileText, Minimize2, Maximize2 } from 'lucide-react';

const AIChatbot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [messages, setMessages] = useState([
    {
      id: 1,
      type: 'bot',
      text: 'Hi! I\'m Cloud AI. How can I help you today?',
      timestamp: new Date()
    }
  ]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);

  const quickActions = [
    { icon: TrendingUp, label: 'View Analytics', query: 'Show me analytics' },
    { icon: BarChart3, label: 'Campaign Info', query: 'Tell me about campaigns' },
    { icon: AlertCircle, label: 'Get Support', query: 'I need help' },
    { icon: FileText, label: 'Pricing', query: 'What are your prices?' }
  ];

  const processQuery = (query) => {
    const lowerQuery = query.toLowerCase();
    
    if (lowerQuery.includes('price') || lowerQuery.includes('cost')) {
      return {
        text: 'Our drone advertising packages start from ₹50,000. Would you like to:\n\n1. See detailed pricing\n2. Request a custom quote\n3. Schedule a demo\n\nVisit our pricing page or contact our team for more details!'
      };
    }
    
    if (lowerQuery.includes('campaign') || lowerQuery.includes('advertising')) {
      return {
        text: 'CloudMins offers cutting-edge drone advertising! We can help you:\n\n• Launch citywide campaigns\n• Target specific audiences\n• Track real-time analytics\n• Maximize ROI\n\nWould you like to start a campaign or see our case studies?'
      };
    }
    
    if (lowerQuery.includes('analytics') || lowerQuery.includes('dashboard')) {
      return {
        text: 'Our analytics dashboard provides:\n\n✓ Real-time engagement metrics\n✓ ROI tracking\n✓ City-wise coverage\n✓ AI-powered insights\n\nLog in to access your dashboard or request a demo to see it in action!'
      };
    }
    
    if (lowerQuery.includes('help') || lowerQuery.includes('support')) {
      return {
        text: 'I\'m here to help! You can:\n\n• Email: support@cloudmins.com\n• Call: +91-XXXXXXXXXX\n• Chat: Right here with me!\n\nWhat would you like assistance with?'
      };
    }
    
    return {
      text: 'I can help you with:\n\n• Campaign information\n• Pricing details\n• Analytics dashboard\n• Technical support\n• Booking a demo\n\nWhat would you like to know?'
    };
  };

  const handleSend = () => {
    if (!inputValue.trim()) return;
    
    const userMessage = {
      id: messages.length + 1,
      type: 'user',
      text: inputValue,
      timestamp: new Date()
    };
    
    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsTyping(true);
    
    setTimeout(() => {
      const response = processQuery(inputValue);
      const botMessage = {
        id: messages.length + 2,
        type: 'bot',
        text: response.text,
        timestamp: new Date()
      };
      setMessages(prev => [...prev, botMessage]);
      setIsTyping(false);
    }, 1000);
  };

  const handleQuickAction = (query) => {
    setInputValue(query);
    setTimeout(() => handleSend(), 100);
  };

  const formatTimestamp = (date) => {
    return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="fixed bottom-4 right-4 sm:bottom-6 sm:right-6 z-50">
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          aria-label="Open chat"
          className="bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-full p-3 sm:p-4 shadow-2xl hover:shadow-blue-500/50 hover:scale-110 transition-all duration-300 group"
        >
          <MessageSquare className="w-5 h-5 sm:w-6 sm:h-6 group-hover:rotate-12 transition-transform" />
        </button>
      )}

      {isOpen && (
        <div className={`bg-white rounded-2xl shadow-2xl border border-gray-200 transition-all duration-300 ${
          isMinimized ? 'w-[280px] sm:w-80 h-16' : 'w-[calc(100vw-2rem)] sm:w-96 h-[500px] sm:h-[600px] max-w-[500px]'
        } flex flex-col`}>
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-4 rounded-t-2xl flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="bg-white/20 p-2 rounded-lg">
                <MessageSquare className="w-5 h-5" />
              </div>
              <div>
                <h3 className="font-bold text-lg">Cloud AI</h3>
                <p className="text-xs text-blue-100">Your Assistant</p>
              </div>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setIsMinimized(!isMinimized)}
                aria-label={isMinimized ? "Maximize chat" : "Minimize chat"}
                className="hover:bg-white/20 p-2 rounded-lg transition-colors"
              >
                {isMinimized ? <Maximize2 className="w-4 h-4" /> : <Minimize2 className="w-4 h-4" />}
              </button>
              <button
                onClick={() => setIsOpen(false)}
                aria-label="Close chat"
                className="hover:bg-white/20 p-2 rounded-lg transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {!isMinimized && (
            <>
              <div className="p-4 bg-gray-50 border-b border-gray-200">
                <p className="text-xs text-gray-600 mb-2 font-medium">Quick Actions:</p>
                <div className="grid grid-cols-2 gap-2">
                  {quickActions.map((action, idx) => (
                    <button
                      key={idx}
                      onClick={() => handleQuickAction(action.query)}
                      className="flex items-center gap-2 p-2 bg-white border border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-all text-xs group"
                    >
                      <action.icon className="w-4 h-4 text-gray-500 group-hover:text-blue-600" />
                      <span className="text-gray-700 group-hover:text-blue-600">{action.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {messages.map((message) => (
                  <div
                    key={message.id}
                    className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-[80%] rounded-2xl p-3 ${
                        message.type === 'user'
                          ? 'bg-blue-600 text-white rounded-br-sm'
                          : 'bg-gray-100 text-gray-800 rounded-bl-sm'
                      }`}
                    >
                      <p className="text-sm whitespace-pre-line">{message.text}</p>
                      <p className={`text-xs mt-1 ${
                        message.type === 'user' ? 'text-blue-100' : 'text-gray-500'
                      }`}>
                        {formatTimestamp(message.timestamp)}
                      </p>
                    </div>
                  </div>
                ))}
                
                {isTyping && (
                  <div className="flex justify-start">
                    <div className="bg-gray-100 rounded-2xl rounded-bl-sm p-3">
                      <div className="flex gap-1">
                        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.4s' }}></div>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="p-4 border-t border-gray-200 bg-white rounded-b-2xl">
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSend()}
                    placeholder="Ask me anything..."
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                  />
                  <button
                    onClick={handleSend}
                    disabled={!inputValue.trim()}
                    aria-label="Send message"
                    className="bg-blue-600 text-white p-2 rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                  >
                    <Send className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default AIChatbot;
